import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, TrendingUp, Home, Building2 } from "lucide-react";

interface PaybackScenario {
  type: 'particulier' | 'mkb' | 'groot-zakelijk';
  label: string;
  icon: React.ReactNode;
  description: string;
}

const scenarios: PaybackScenario[] = [
  {
    type: 'particulier',
    label: 'Particulier',
    icon: <Home className="w-5 h-5" />,
    description: 'Woning met gemiddeld energieverbruik'
  },
  {
    type: 'mkb',
    label: 'MKB Bedrijf',
    icon: <Building2 className="w-5 h-5" />,
    description: 'Klein tot middelgroot bedrijf'
  },
  {
    type: 'groot-zakelijk',
    label: 'Groot Zakelijk',
    icon: <TrendingUp className="w-5 h-5" />,
    description: 'Grote organisatie of industrie'
  }
];

export function PaybackCalculator() {
  const [scenario, setScenario] = useState<string>('');
  const [investment, setInvestment] = useState<string>('');
  const [yearlyUsage, setYearlyUsage] = useState<string>('');
  const [energyPrice, setEnergyPrice] = useState<string>('0.35');
  const [results, setResults] = useState<any>(null);

  const calculatePayback = () => {
    const investmentAmount = parseFloat(investment);
    const usage = parseFloat(yearlyUsage);
    const price = parseFloat(energyPrice);

    if (!investmentAmount || !usage || !price) return;

    // Basis berekening met scenario-specifieke parameters
    const scenarioMultipliers = {
      'particulier': { efficiency: 0.85, subsidy: 0.21 },
      'mkb': { efficiency: 0.88, subsidy: 0.15 },
      'groot-zakelijk': { efficiency: 0.90, subsidy: 0.12 }
    };

    const multiplier = scenarioMultipliers[scenario as keyof typeof scenarioMultipliers] || scenarioMultipliers.particulier;
    
    const yearlyProduction = usage * multiplier.efficiency;
    const yearlySavings = yearlyProduction * price;
    const netInvestment = investmentAmount * (1 - multiplier.subsidy);
    const paybackYears = netInvestment / yearlySavings;
    const totalSavings25Years = (yearlySavings * 25) - netInvestment;

    setResults({
      paybackYears: Math.round(paybackYears * 10) / 10,
      yearlySavings: Math.round(yearlySavings),
      totalSavings25Years: Math.round(totalSavings25Years),
      yearlyProduction: Math.round(yearlyProduction),
      netInvestment: Math.round(netInvestment)
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Calculator className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">Terugverdientijd Calculator</h2>
        </div>
        <p className="text-muted-foreground">
          Bereken de terugverdientijd van uw zonnepanelen voor verschillende scenario's
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Scenario Selectie</CardTitle>
            <CardDescription>Kies uw situatie voor een accurate berekening</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              {scenarios.map((s) => (
                <div
                  key={s.type}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    scenario === s.type
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setScenario(s.type)}
                >
                  <div className="flex items-center gap-3">
                    {s.icon}
                    <div>
                      <div className="font-medium">{s.label}</div>
                      <div className="text-sm text-muted-foreground">{s.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="investment">Investering (€)</Label>
                <Input
                  id="investment"
                  type="number"
                  placeholder="20000"
                  value={investment}
                  onChange={(e) => setInvestment(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="usage">Jaarlijks energieverbruik (kWh)</Label>
                <Input
                  id="usage"
                  type="number"
                  placeholder="3500"
                  value={yearlyUsage}
                  onChange={(e) => setYearlyUsage(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="price">Energieprijs (€/kWh)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  placeholder="0.35"
                  value={energyPrice}
                  onChange={(e) => setEnergyPrice(e.target.value)}
                />
              </div>

              <Button 
                onClick={calculatePayback} 
                className="w-full"
                disabled={!scenario || !investment || !yearlyUsage}
              >
                Bereken Terugverdientijd
              </Button>
            </div>
          </CardContent>
        </Card>

        {results && (
          <Card>
            <CardHeader>
              <CardTitle>Resultaten</CardTitle>
              <CardDescription>Uw persoonlijke terugverdientijd berekening</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center space-y-2">
                <div className="text-3xl font-bold text-primary">
                  {results.paybackYears} jaar
                </div>
                <div className="text-muted-foreground">Terugverdientijd</div>
              </div>

              <div className="grid gap-3">
                <div className="flex justify-between p-3 bg-muted/50 rounded">
                  <span>Netto investering:</span>
                  <span className="font-medium">€{results.netInvestment.toLocaleString()}</span>
                </div>
                <div className="flex justify-between p-3 bg-muted/50 rounded">
                  <span>Jaarlijkse opbrengst:</span>
                  <span className="font-medium">{results.yearlyProduction.toLocaleString()} kWh</span>
                </div>
                <div className="flex justify-between p-3 bg-muted/50 rounded">
                  <span>Jaarlijkse besparing:</span>
                  <span className="font-medium">€{results.yearlySavings.toLocaleString()}</span>
                </div>
                <div className="flex justify-between p-3 bg-primary/10 rounded">
                  <span>Totale besparing (25 jaar):</span>
                  <span className="font-medium text-primary">
                    €{results.totalSavings25Years.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded">
                <p className="font-medium mb-1">Opmerking:</p>
                <p>
                  Deze berekening is indicatief en gebaseerd op gemiddelde waarden. 
                  Voor een nauwkeurige berekening nemen wij graag contact met u op.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}